import { logZodValidationError } from '@/api/utils';
import { z } from 'zod';

import { ApiError } from './ApiError';

// ASP.NET Core ProblemDetails plus a custom `errorDetails` extension (FluentValidation-style field
// path -> messages), e.g. { "model.CounterpartyId": ["'Counterparty Id' must not be empty."] }.
// api/client.ts's interceptor only understands the `ErrorMessage`/`Error` envelope, so for this shape
// ApiError.message degrades to axios's generic "Request failed with status code 400" — the actual
// validation text survives only on `ApiError.response`, read back out here. Shared by
// extractValidationMessage (flat joined text) and parseFieldErrorDetails (the raw per-field map).
const fieldErrorsSchema = z.record(z.string(), z.array(z.string()));

// Every consumer of errorDetails downstream (extractValidationMessage, parseFieldErrorDetails,
// mapBackendFieldErrors, and transitively FixtureValidationBanner/ValidityField's rendering) trusts
// this shape without re-checking it — a malformed errorDetails (a string, an object with non-array
// values, etc., from a misbehaving backend, gateway, or proxy error page) would otherwise reach a
// `.map()` call mid-render and crash the whole page. Validated here, once, at the read boundary
// (unlike validateResponse's success-path schemas, this must NOT throw on failure — a malformed
// errorDetails degrades to the same "no details" fallback every consumer already has for a plain
// 500, rather than crashing the render), logging via the same logZodValidationError every other
// boundary uses so a misbehaving backend/gateway doesn't fail silently.
const isProblemDetailsWithErrors = (value: unknown): value is { errorDetails?: Record<string, string[]> } => {
  if (typeof value !== 'object' || value === null) return false;
  const errorDetails = (value as { errorDetails?: unknown }).errorDetails;
  if (errorDetails === undefined) return true;
  const result = fieldErrorsSchema.safeParse(errorDetails);
  if (!result.success) logZodValidationError(result.error, errorDetails);
  return result.success;
};

export const errorDetailsOf = (error: unknown): Record<string, string[]> | undefined => {
  if (!(error instanceof ApiError) || !isProblemDetailsWithErrors(error.response)) return undefined;
  return error.response.errorDetails;
};
